/**
 * Connect preview / test-send optional personalization (ENG-1291).
 * When present on the wire, all three fields are required by the API.
 */
export type PreviewFillParams = {
  user_id: string;
  dragon_tile_id: string;
  inner_tile_id: string;
};

/** API `fallback_reason` string (proto enum serialized by Connect). */
export type PreviewFallbackReason = string;

/** Placement fields required alongside a committed profile to send `preview_fill_params`. */
export type PreviewUserContextPlacement = {
  dragonTileId: string;
  innerTileId: string;
};

export type PreviewProfileId = { id: string };

/**
 * Returns API-ready `preview_fill_params` only when the profile and placement are complete.
 */
export function buildPreviewFillParams(
  profile: PreviewProfileId | null | undefined,
  placement: PreviewUserContextPlacement | undefined,
): PreviewFillParams | undefined {
  if (!placement) return undefined;
  const userId = typeof profile?.id === "string" ? profile.id.trim() : "";
  const dragon = placement.dragonTileId.trim();
  const inner = placement.innerTileId.trim();
  if (!userId || !dragon || !inner) return undefined;
  return {
    user_id: userId,
    dragon_tile_id: dragon,
    inner_tile_id: inner,
  };
}

/**
 * User-visible copy when the renderer used sample / fallback data (ENG-1291).
 */
export function describePreviewSampleDataMessage(args: {
  usedFallback?: boolean;
  fallbackReason?: string;
}): string | null {
  if (!args.usedFallback) return null;
  const reason = args.fallbackReason?.trim().toUpperCase() ?? "";
  switch (reason) {
    case "EMPTY":
      return "This preview uses sample data because no personalized content was available for this profile.";
    case "FAILED":
      return "This preview uses sample data because personalized content could not be loaded.";
    case "TIMEOUT":
      return "This preview uses sample data because loading personalized content timed out.";
    case "NO_CONTEXT":
      return "This preview uses sample data because no profile context was sent.";
    case "NONE":
    default:
      return "This preview uses sample data.";
  }
}

const CONTEXT_KEY_OMITTED = "none" as const;

/** Stable React Query key segments when `preview_fill_params` is omitted or partial guard. */
export function previewUserContextKeyParts(
  ctx?: PreviewFillParams,
): readonly [string, string, string] {
  if (!ctx) {
    return [CONTEXT_KEY_OMITTED, CONTEXT_KEY_OMITTED, CONTEXT_KEY_OMITTED];
  }
  const userId = typeof ctx.user_id === "string" ? ctx.user_id.trim() : "";
  const dragon =
    typeof ctx.dragon_tile_id === "string" ? ctx.dragon_tile_id.trim() : "";
  const inner =
    typeof ctx.inner_tile_id === "string" ? ctx.inner_tile_id.trim() : "";
  return [
    userId.length > 0 ? userId : CONTEXT_KEY_OMITTED,
    dragon.length > 0 ? dragon : CONTEXT_KEY_OMITTED,
    inner.length > 0 ? inner : CONTEXT_KEY_OMITTED,
  ];
}
